import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, AlertTriangle, CheckCircle2, Clock, BookOpen,
  ChevronRight, BarChart3, Shield, Eye, Activity, TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Progress } from "../../components/ui/progress";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/manager/dashboard");
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <AdminLayout title="Team Dashboard" variant="manager"><div className="flex items-center justify-center py-24"><Clock className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;

  const s = data?.summary || {};
  const reports = data?.direct_reports || [];
  const overdueReports = reports.filter(r => r.compliance_overdue > 0);

  return (
    <AdminLayout title="Team Dashboard" variant="manager">
      <div className="space-y-6" data-testid="manager-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="manager-dashboard-title">
              Team Dashboard
            </h1>
            <p className="text-muted-foreground">Welcome back, {user?.full_name}. Here's how your team is doing.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => navigate("/manager/team")} data-testid="nav-team-roster">
              <Users className="w-3.5 h-3.5" /> Team Roster
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => navigate("/my-courses")} data-testid="nav-my-courses">
              <BookOpen className="w-3.5 h-3.5" /> My Courses
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => navigate("/manager/team-analytics")} data-testid="nav-analytics">
              <BarChart3 className="w-3.5 h-3.5" /> Team Analytics
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Direct Reports", value: s.total_reports, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
            { label: "Active", value: s.active, icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10" },
            { label: "Overdue Compliance", value: s.total_overdue_compliance, icon: AlertTriangle, color: s.total_overdue_compliance > 0 ? "text-red-500" : "text-green-500", bg: s.total_overdue_compliance > 0 ? "bg-red-500/10" : "bg-green-500/10" },
            { label: "Avg Completion", value: `${s.avg_completion_rate}%`, icon: BarChart3, color: "text-primary", bg: "bg-primary/10" },
          ].map((st, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${st.bg} flex items-center justify-center`}>
                      <st.icon className={`w-5 h-5 ${st.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{st.value}</p>
                      <p className="text-xs text-muted-foreground">{st.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Compliance Alerts */}
        {overdueReports.length > 0 && (
          <Card className="border-red-500/20 bg-red-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {overdueReports.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-background border border-red-500/10">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-xs font-bold text-red-500">
                        {r.full_name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{r.full_name}</p>
                        <p className="text-xs text-red-500">{r.compliance_overdue} overdue item(s)</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-500/20" onClick={() => navigate(`/manager/team/${r.id}`)} data-testid={`alert-${r.id}`}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Direct Reports */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> My Team ({reports.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No direct reports assigned</p>
                <p className="text-sm mt-1">Ask your Business Admin to assign team members to you</p>
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map((r, i) => (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex items-center justify-between p-4 rounded-xl border hover:border-primary/20 cursor-pointer transition-all group"
                    onClick={() => navigate(`/manager/team/${r.id}`)}
                    data-testid={`report-${r.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${r.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {r.full_name[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{r.full_name}</p>
                          {r.status !== "active" && <Badge variant="outline" className="text-[10px] text-red-500">{r.status}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{r.job_title || r.email}{r.department ? ` · ${r.department}` : ""}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right hidden md:block">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{r.completed_courses}/{r.total_courses}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Courses completed</p>
                      </div>

                      <div className="w-20 hidden md:block">
                        <Progress value={r.completion_rate} className="h-2" />
                        <p className="text-[10px] text-muted-foreground text-right mt-0.5">{r.completion_rate}%</p>
                      </div>

                      {r.compliance_overdue > 0 && (
                        <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
                          {r.compliance_overdue} overdue
                        </Badge>
                      )}

                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ManagerDashboard;
